import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Calendar,
  Users,
  ArrowLeft,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import StepContainer from '../../components/StepContainer';
import Card from '../../components/Card';
import Button from '../../components/Button';
import AudienceCardSkeleton from '../../components/skeletons/AudienceCardSkeleton';
import { useAudienceData, useAudiencePagination } from '../../contexts/AudienceContext';

interface Segment {
  id: number;
  name: string;
  persona_count: number;
  sub_industry?: string;
}

interface Audience {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  len: number; // Number of segments
  total_personas?: number;
  segments?: Segment[];
  website?: string;
  business_description?: string;
  industry?: string;
  core_need?: string;
  additional_info?: string;
  audience_purpose?: string;
  customers?: string;
  location?: string;
  age_range?: string;
  income_level?: string;
}

interface ExistingAudiencesProps {
  onSelectAudience: (audienceId: number, audienceName: string) => void;
  onBack: () => void;
  /** Take the user to the build-audience flow. Required for the zero-audience state to
   *  offer a way forward — see the empty state below (#527 P1-05). */
  onCreateAudience?: () => void;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const ExistingAudiences: React.FC<ExistingAudiencesProps> = ({
  onSelectAudience,
  onBack,
  onCreateAudience,
}) => {
  const [selectedAudience, setSelectedAudience] = useState<number | null>(null);
  const [expandedAudiences, setExpandedAudiences] = useState<(number | string)[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [audienceData, setAudienceData] = useState<{
    count: number;
    limit: number;
    can_create: boolean;
    plan: string;
  } | null>(null);
  // True only once a load has actually FINISHED. The previous flag was inferred from
  // `progressiveTotal >= 0`, which is true of the reducer's INITIAL state (that field starts at 0), while
  // `progressiveLoading` does not turn true until the id request comes back — so for the whole width of a
  // network round-trip this screen told a user with audiences that they had none. An unresolved request is
  // not an answer.
  const [loadSettled, setLoadSettled] = useState(false);

  const {
    loading,
    error,
    filters,
    setFilters,
    loadAudiencesProgressively,
    progressiveAudiences,
    progressiveTotal,
    progressiveLoading,
  } = useAudienceData();

  const { paginatedData, currentPage, pageSize, setPage, setPageSize } = useAudiencePagination();

  // Available page sizes
  const pageSizeOptions = [10, 20, 50];

  /**
   * Every audience load goes through here so "is a request in flight?" is a fact rather than an inference.
   * The context's own `progressiveLoading` only turns true after the id request returns, which leaves the
   * first — and longest — part of the load looking identical to "finished, and there is nothing".
   */
  const runAudienceLoad = useCallback(
    async (page?: number, size?: number, activeFilters?: Record<string, string>) => {
      setLoadSettled(false);
      try {
        await loadAudiencesProgressively(page, size, activeFilters);
      } finally {
        // In the `finally`, so a failed load still stops claiming the account is empty — the error banner
        // above the list is what explains it, and `!error` gates the empty state as well.
        setLoadSettled(true);
      }
    },
    [loadAudiencesProgressively],
  );

  // Debounced search and filter application
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const newFilters = {
        ...(searchTerm && { search: searchTerm }),
        ...(industryFilter && { industry: industryFilter }),
      };

      if (JSON.stringify(newFilters) !== JSON.stringify(filters)) {
        setFilters(newFilters);
        runAudienceLoad(1, pageSize, newFilters);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, industryFilter, pageSize, filters, runAudienceLoad]);

  // Load initial data and audience count
  useEffect(() => {
    runAudienceLoad();

    // Fetch audience count and limit
    const fetchAudienceData = async () => {
      try {
        const response = await fetch(`${API_URL}/audience/count`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setAudienceData(data);
        }
      } catch (error) {
        console.error('Error fetching audience data:', error);
      }
    };

    fetchAudienceData();
  }, []);

  // Get unique industries for filter dropdown
  const availableIndustries = useMemo(() => {
    const audiences = progressiveAudiences.filter((a) => a !== null) as Audience[];
    if (audiences.length === 0 && paginatedData?.audiences) {
      audiences.push(...paginatedData.audiences);
    }

    const industries = new Set<string>();
    audiences.forEach((audience) => {
      // Add null check for audience object
      if (audience && audience.industry) {
        const industryList = audience.industry.split(',').map((i) => i.trim());
        industryList.forEach((industry) => industries.add(industry));
      }
    });
    return Array.from(industries).sort();
  }, [progressiveAudiences, paginatedData?.audiences]);

  // Determine what audiences to display
  const displayAudiences = useMemo(() => {
    if (progressiveAudiences.length > 0) {
      return progressiveAudiences;
    }
    return paginatedData?.audiences || [];
  }, [progressiveAudiences, paginatedData]);

  const totalCount = progressiveTotal || paginatedData?.total || 0;

  const handleSelectAudience = (audience: Audience) => {
    setSelectedAudience(audience.id);
    setTimeout(() => {
      onSelectAudience(audience.id, audience.name);
    }, 300);
  };

  const toggleExpandAudience = (audienceId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedAudiences((prev) =>
      prev.includes(audienceId) ? prev.filter((id) => id !== audienceId) : [...prev, audienceId],
    );
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    runAudienceLoad(newPage, pageSize, filters);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    runAudienceLoad(1, newPageSize, filters);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setIndustryFilter('');
    setFilters({});
    runAudienceLoad(1, pageSize, {});
  };

  return (
    <StepContainer title="Select an Existing Audience" className="animate-fadeIn">
      <div className="mb-6 flex justify-between items-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          icon={<ArrowLeft className="w-4 h-4 mr-1" />}
        >
          Back
        </Button>

        {/* Audience-CREATION allowance — a count of audiences this plan may still BUILD, not of audiences
            the account has. Hidden entirely for a plan whose allowance is 0, because "Audiences: 0 / 0"
            beside "Showing 1 to 1 of 1 audiences" reads as "you have no audiences", and an upsell is no use
            to a cohort with no self-serve billing. A plan with a real limit keeps both, exhausted or not. */}
        {audienceData && audienceData.limit > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Audiences:</span>
            <span
              className={`px-2 py-1 rounded-full font-medium ${
                audienceData.can_create ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}
            >
              {audienceData.count} / {audienceData.limit === 0 ? '0' : audienceData.limit}
            </span>
            {!audienceData.can_create && (
              <span className="text-xs text-gray-500">
                {audienceData.limit === 0
                  ? '(Upgrade to create audiences)'
                  : '(Limit reached - upgrade for more)'}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search audiences by name, description, or industry..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>

          {/* Filter Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            icon={<Filter className="w-4 h-4" />}
            className={showFilters ? 'bg-blue-50' : ''}
          >
            Filters {(searchTerm || industryFilter) && '(Active)'}
          </Button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex-1 min-w-48">
                {/* `htmlFor`/`id`: these labels were floating text, so neither select had an accessible
                    name — a screen reader read "combobox" twice with nothing to tell them apart. */}
                <label
                  htmlFor="audience-industry-filter"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Industry
                </label>
                <select
                  id="audience-industry-filter"
                  value={industryFilter}
                  onChange={(e) => setIndustryFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Industries</option>
                  {availableIndustries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-32">
                <label
                  htmlFor="audience-page-size"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Results per page
                </label>
                <select
                  id="audience-page-size"
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {(searchTerm || industryFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-blue-600 hover:text-blue-800"
              >
                Clear all filters
              </Button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {loadSettled &&
        !progressiveLoading &&
        !loading &&
        displayAudiences.filter((a) => a !== null).length === 0 &&
        !error && (
          <div className="text-center py-12">
            {/* A new client with zero audiences used to land here on a BLANK panel: an empty
              <p>{null}</p> and a commented-out CTA. Every audience-based Simulate form
              funnels into this screen, so finishing a survey and arriving at nothing was a
              dead end with no message and no way out (#527 P1-05).

              The old CTA was commented out for a good reason — it was wired to onBack,
              which returns to the form, so it never led anywhere. It now goes to the
              build-audience flow, which is the actual next step. */}
            <p className="text-gray-500">
              {searchTerm || industryFilter
                ? 'No audiences match your search criteria.'
                : 'You don\u2019t have any audiences yet. Build one to run this study against.'}
            </p>
            {!(searchTerm || industryFilter) ? (
              onCreateAudience ? (
                <Button variant="outline" className="mt-4" onClick={onCreateAudience}>
                  Create Your First Audience
                </Button>
              ) : null
            ) : (
              <Button variant="outline" className="mt-4" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        )}

      {(displayAudiences.filter((a) => a !== null).length > 0 ||
        (progressiveLoading && displayAudiences.length > 0)) && (
        <>
          {totalCount > 0 && (
            <div className="mb-4 text-sm text-gray-600">
              Showing {(currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, totalCount)} of {totalCount} audiences
              {progressiveLoading && progressiveAudiences.filter((a) => a !== null).length > 0 && (
                <span className="ml-2 text-blue-600">
                  (Loaded {progressiveAudiences.filter((a) => a !== null).length} of{' '}
                  {Math.min(pageSize, totalCount - (currentPage - 1) * pageSize)})
                </span>
              )}
            </div>
          )}

          <div className="grid gap-6">
            {displayAudiences.map((audience, index) =>
              audience === null ? (
                <AudienceCardSkeleton key={`skeleton-${index}`} />
              ) : (
                <Card
                  key={audience.id}
                  onClick={() => handleSelectAudience(audience)}
                  selected={selectedAudience === audience.id}
                  className={`p-0 hover:shadow-xl transition-all duration-200 cursor-pointer border ${selectedAudience === audience.id ? 'border-blue-300 ring-2 ring-blue-200' : 'border-gray-100'} overflow-hidden`}
                  fullWidth
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Left Column - Header & Primary Info */}
                    <div className="p-5 md:w-1/3 bg-gradient-to-br from-blue-50 to-white border-r border-gray-100">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{audience.name}</h3>

                      <div className="flex items-center text-sm text-gray-600 mb-4">
                        <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                        <span>
                          Created{' '}
                          {new Date(audience.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>

                      {/* Core Stats */}
                      <div className="space-y-3">
                        {/* Personas */}
                        <div className="bg-white rounded-md p-3 flex items-center shadow-sm">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <Users className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Total Profiles</p>
                            <p className="text-lg font-medium">
                              {audience.total_personas || audience.len * 5 || 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* Segments — hidden when there is only one. "Segments: 1" tells the reader nothing, and
                      every AI-summit audience is a single segment, so the tile was pure noise on those cards. */}
                        {audience.len > 1 && (
                          <div className="bg-white rounded-md p-3 flex items-center shadow-sm">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-indigo-600"
                              >
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Segments</p>
                              <p className="text-lg font-medium">{audience.len}</p>
                            </div>
                          </div>
                        )}

                        {/* Industry */}
                        {audience.industry && (
                          <div className="bg-white rounded-md p-3 flex items-center shadow-sm">
                            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center mr-3">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-emerald-600"
                              >
                                <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path>
                                <path d="M13 5v16"></path>
                              </svg>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Industry</p>
                              <p className="text-xs font-medium">
                                {audience.industry?.includes(',')
                                  ? (() => {
                                      const industries = audience.industry.split(',');
                                      return (
                                        <>
                                          {industries
                                            .slice(
                                              0,
                                              expandedAudiences.includes(`ind-${audience.id}`)
                                                ? industries.length
                                                : 4,
                                            )
                                            .map((ind, i) => (
                                              <span key={`${audience.id}-ind-${i}`}>
                                                {ind.trim().replace(/_/g, ' ')}
                                                {i <
                                                Math.min(
                                                  industries.length,
                                                  expandedAudiences.includes(`ind-${audience.id}`)
                                                    ? industries.length
                                                    : 4,
                                                ) -
                                                  1
                                                  ? ', '
                                                  : ''}
                                              </span>
                                            ))}
                                          {industries.length > 4 &&
                                            !expandedAudiences.includes(`ind-${audience.id}`) && (
                                              <span
                                                className="text-blue-600 cursor-pointer"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setExpandedAudiences((prev) => [
                                                    ...prev,
                                                    `ind-${audience.id}`,
                                                  ]);
                                                }}
                                              >
                                                {' '}
                                                +{industries.length - 4} more
                                              </span>
                                            )}
                                          {industries.length > 4 &&
                                            expandedAudiences.includes(`ind-${audience.id}`) && (
                                              <span
                                                className="text-blue-600 cursor-pointer"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setExpandedAudiences((prev) =>
                                                    prev.filter(
                                                      (id) => id !== `ind-${audience.id}`,
                                                    ),
                                                  );
                                                }}
                                              >
                                                {' '}
                                                (show less)
                                              </span>
                                            )}
                                        </>
                                      );
                                    })()
                                  : audience.industry?.replace(/_/g, ' ')}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Select Button for small screens */}
                      <Button
                        variant="primary"
                        className="w-full mt-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 md:hidden"
                        onClick={() => handleSelectAudience(audience)}
                      >
                        Select Audience
                      </Button>
                    </div>

                    {/* Right Column - Detailed Info */}
                    <div className="p-5 md:w-2/3 flex flex-col">
                      <div className="flex-1">
                        {/* Industry Segments Breakdown */}
                        {audience.industry && (
                          <div className="mb-5">
                            <h4 className="text-md font-medium text-gray-700 mb-3 pb-2 border-b border-gray-100 flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="mr-2 text-blue-500"
                              >
                                <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path>
                                <path d="M13 5v16"></path>
                              </svg>
                              Industry Segments
                            </h4>
                            <div className="bg-gray-50 p-3 rounded-md">
                              {audience.segments ? (
                                <>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {audience.segments
                                      .slice(
                                        0,
                                        expandedAudiences.includes(audience.id)
                                          ? audience.segments.length
                                          : 4,
                                      )
                                      .map((segment, index) => (
                                        <div
                                          key={`${audience.id}-segment-${segment.id}-${index}`}
                                          className="relative group flex justify-between items-center bg-white p-1.5 rounded border border-gray-100 hover:bg-gray-50 transition-colors"
                                        >
                                          <span className="text-gray-700 text-xs font-medium truncate max-w-[65%]">
                                            {segment.name?.replace(/_/g, ' ') || 'General'}
                                          </span>
                                          <span className="bg-blue-100 text-blue-700 text-xs py-0.5 px-1.5 rounded-full whitespace-nowrap">
                                            {segment.persona_count}
                                          </span>
                                          {/* Tooltip */}
                                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                                            {segment.name?.replace(/_/g, ' ') || 'General'} (
                                            {segment.persona_count} personas)
                                            <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                  {audience.segments.length > 4 && (
                                    <div
                                      className="mt-2 text-xs text-blue-600 hover:text-blue-800 cursor-pointer font-medium"
                                      onClick={(e) => toggleExpandAudience(audience.id, e)}
                                    >
                                      {expandedAudiences.includes(audience.id)
                                        ? 'Show less'
                                        : `+ ${audience.segments.length - 4} more segments`}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="text-xs text-gray-600 py-2 px-3 bg-white rounded border border-gray-100">
                                  {audience.industry?.replace(/_/g, ' ')} - {audience.len} industry
                                  segments
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Additional Information */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                          {/* Business Context */}
                          {audience.business_description && (
                            <div className="col-span-2">
                              <h5 className="text-sm font-medium text-gray-500 mb-1">
                                Business Description
                              </h5>
                              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md border border-gray-100">
                                {audience.business_description}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Select Button for larger screens */}
                      <div className="mt-5 hidden md:block">
                        <Button
                          variant="primary"
                          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                          onClick={() => handleSelectAudience(audience)}
                        >
                          Select This Audience
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ),
            )}
          </div>

          {/* Pagination Controls */}
          {totalCount > pageSize && (
            <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                Page {currentPage} of {Math.ceil(totalCount / pageSize)}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  icon={<ChevronLeft className="w-4 h-4" />}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, Math.ceil(totalCount / pageSize)) }, (_, i) => {
                    const totalPages = Math.ceil(totalCount / pageSize);
                    let pageNum;

                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 text-sm rounded-md border transition-colors ${
                          currentPage === pageNum
                            ? 'bg-blue-500 text-white border-blue-500'
                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                  icon={<ChevronRight className="w-4 h-4" />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </StepContainer>
  );
};

export default ExistingAudiences;
